import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BundleTreeType,
  BundleTreeTypes,
  ChevronIcon,
  CloseIcon,
  Dialog,
  SectionRenameInput,
  Toast,
  globalToastService,
} from '@repo/atomic-components';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { GreenButton } from '@/ui/components/GreenButton';
import { classNames } from 'primereact/utils';
import {
  DEFAULT_NEW_SECTION_NAME,
  SectionItem,
  useMoveSectionsDialogLogic,
  useViewportPageIndexContext,
} from '../hooks';
import { CATEGORY_SUGGESTIONS } from '@/constants';
import { isEndpointLockError, useAddBundlePageCategoryMutation } from '@/api';
import {
  makeSelectBundlePageIndexByPageId,
  selectBundleSectionsSuggestionsCategoryList,
  selectBundleSectionsSuggestionsSubCategoryList,
  selectSharedDataBundleId,
  useAppSelector,
  useAppStore,
} from '@/store';
import { BROADCAST_FORCE_CATEGORY_LIST_REFRESH, useFletchersBroadcast } from '@/broadcast';
import { showPagesMovedToast } from '../utils';

interface MoveSectionsDialogProps {
  setVisible: (value: boolean) => void;
  pageIds: string[];
}

export const MoveSectionsDialog: FC<MoveSectionsDialogProps> = ({ pageIds, setVisible }) => {
  const toast = useRef<Toast>(null);
  const { sendMessageWithoutResponse } = useFletchersBroadcast();
  const store = useAppStore();
  const documentReviewPageContext = useViewportPageIndexContext();
  const bundleId = useAppSelector(selectSharedDataBundleId);
  const suggestionsMapWithBundleSectionsCategoryList = useAppSelector(selectBundleSectionsSuggestionsCategoryList);
  const suggestionsMapWithBundleSectionsSubCategoryList = useAppSelector(
    selectBundleSectionsSuggestionsSubCategoryList,
  );

  const [triggerAddBundlePageCategory] = useAddBundlePageCategoryMutation();

  const {
    newSectionId,
    newSectionName,
    setNewSectionName,
    newSectionType,
    newSectionIndex,
    newSectionCategoryId,
    isNewSectionAdded,
    providerId,
    currentListItems,
    currentListItemsType,
    topLevelElement,
    showBackButton,
    isNewSectionNameAlreadyExistInCurrentType,
    handleAddNewSectionClick,
    handleRemoveNewSectionClick,
    handleOpenSubcategoriesClick,
    handleBackToCategoriesClick,
    setIsNewSectionNameAlreadyExistInCurrentType,
    moveNewSectionToIndex,
  } = useMoveSectionsDialogLogic({ pageIds });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);
  const [selectedExistingSectionId, setSelectedExistingSectionId] = useState<string | null>(null);
  const [selectedExistingSectionType, setSelectedExistingSectionType] = useState<BundleTreeType | null>(null);
  const [selectedExistingSectionCategoryId, setSelectedExistingSectionCategoryId] = useState<string | null>(null);

  const [isInsertMode, setIsInsertMode] = useState(false);
  // Row index the confirmed new-section is being dragged over, so we can
  // render the same "drop here" line that the bundle sections tree shows.
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Match the sections tree exactly: same hardcoded CATEGORY_SUGGESTIONS
  // list for both Category and Sub-category renames. See BundleSectionsTree
  // where suggestionsMap is built the same way.
  const renameSuggestions = useMemo(() => CATEGORY_SUGGESTIONS.map((name) => ({ id: name, name })), []);

  useEffect(() => {
    if (!isNewSectionAdded) {
      setSelectedSuggestionId(null);
      setNewSectionName('');
    }
  }, [isNewSectionAdded, setNewSectionName]);

  const clearExistingSelection = useCallback(() => {
    setSelectedExistingSectionId(null);
    setSelectedExistingSectionType(null);
    setSelectedExistingSectionCategoryId(null);
  }, []);

  const clearInlineEditor = useCallback(() => {
    setEditingIndex(null);
  }, []);

  const handleHide = useCallback(() => {
    clearInlineEditor();
    clearExistingSelection();
    setVisible(false);
  }, [clearExistingSelection, clearInlineEditor, setVisible]);

  const handleInsertAtPosition = useCallback(
    (index: number) => {
      if (isNewSectionAdded) return;
      clearExistingSelection();
      setEditingIndex(index);
      setSelectedSuggestionId(null);
      setIsInsertMode(false);
    },
    [clearExistingSelection, isNewSectionAdded],
  );

  const handleInlineCancel = useCallback(() => {
    clearInlineEditor();
    setIsInsertMode(false);
    if (editingIndex !== null) {
      setSelectedSuggestionId(null);
    }
  }, [clearInlineEditor, editingIndex]);

  const removeNewInlineSection = useCallback(() => {
    if (!isNewSectionAdded) {
      return;
    }

    handleRemoveNewSectionClick({
      id: newSectionId,
      name: newSectionName || DEFAULT_NEW_SECTION_NAME,
      type: newSectionType ?? currentListItemsType,
    });
    setSelectedSuggestionId(null);
    setNewSectionName('');
  }, [
    handleRemoveNewSectionClick,
    isNewSectionAdded,
    currentListItemsType,
    newSectionId,
    newSectionName,
    newSectionType,
    setNewSectionName,
  ]);

  const handleExistingSectionClick = useCallback(
    (item: SectionItem) => {
      if (editingIndex !== null || item.id === newSectionId) {
        return;
      }

      if (selectedExistingSectionId === item.id) {
        clearExistingSelection();
        return;
      }

      if (isNewSectionAdded) {
        removeNewInlineSection();
      }

      setSelectedExistingSectionId(item.id);
      setSelectedExistingSectionType(item.type);
      if (item.type === BundleTreeTypes.SUB_CATEGORY) {
        const parentCategoryId =
          topLevelElement && topLevelElement.type === BundleTreeTypes.CATEGORY
            ? topLevelElement.id
            : newSectionCategoryId;
        setSelectedExistingSectionCategoryId(parentCategoryId ?? null);
      } else {
        setSelectedExistingSectionCategoryId(null);
      }
    },
    [
      clearExistingSelection,
      editingIndex,
      isNewSectionAdded,
      newSectionCategoryId,
      newSectionId,
      removeNewInlineSection,
      selectedExistingSectionId,
      topLevelElement,
    ],
  );

  const handleInlineSubmit = useCallback(
    (finalValue: string | { id: string; name: string }) => {
      if (editingIndex === null) return;

      // The rename autocomplete's `id` is a placeholder (id === name)
      // pulled from the hardcoded CATEGORY_SUGGESTIONS list, not a real
      // backend UUID. Always drop it and let handleMove re-resolve the
      // typed name against the correct-typed backend list — that's the
      // same pattern the tree's rename flow uses.
      const nextName = typeof finalValue === 'string' ? finalValue.trim() : finalValue.name.trim();

      if (!nextName) {
        toast.current?.show({
          severity: 'warn',
          detail: 'Section name cannot be empty',
          life: 3000,
        });
        return;
      }

      const added = handleAddNewSectionClick(editingIndex, nextName);
      if (!added) return;

      setNewSectionName(nextName);
      setSelectedSuggestionId(null);
      setIsInsertMode(false);
      clearInlineEditor();
      clearExistingSelection();
    },
    [clearExistingSelection, clearInlineEditor, editingIndex, handleAddNewSectionClick, setNewSectionName],
  );

  const resolveSuggestionId = useCallback(
    (name: string, type: BundleTreeType) => {
      const normalized = name.toLowerCase();
      const source =
        type === BundleTreeTypes.CATEGORY
          ? suggestionsMapWithBundleSectionsCategoryList
          : suggestionsMapWithBundleSectionsSubCategoryList;

      const match = source.find((item) => item.name.toLowerCase() === normalized);
      return match?.id ?? null;
    },
    [suggestionsMapWithBundleSectionsCategoryList, suggestionsMapWithBundleSectionsSubCategoryList],
  );

  const handleRemoveInsertedSection = useCallback(
    (item: SectionItem) => {
      handleRemoveNewSectionClick(item);
      setSelectedSuggestionId(null);
      setNewSectionName('');
    },
    [handleRemoveNewSectionClick, setNewSectionName],
  );

  const handleMove = useCallback(() => {
    if (!bundleId || !providerId || !pageIds.length) {
      return;
    }

    if (!isNewSectionAdded && !selectedExistingSectionId) {
      toast.current?.show({
        severity: 'warn',
        detail: 'Select where to move the pages',
        life: 3000,
      });
      return;
    }

    const payload = {
      bundleId,
      providerSectionId: providerId,
      categoryId: null as string | null,
      categoryName: null as string | null,
      newCategoryIndex: null as number | null,
      subCategoryId: null as string | null,
      subCategoryName: null as string | null,
      newSubCategoryIndex: null as number | null,
      pageIds,
    };

    if (isNewSectionAdded) {
      if (newSectionType === null || typeof newSectionIndex !== 'number') {
        toast.current?.show({
          severity: 'warn',
          detail: 'Select where to move the pages',
          life: 3000,
        });
        return;
      }

      const trimmedName = (newSectionName || '').trim() || DEFAULT_NEW_SECTION_NAME;
      // Always resolve against the correct-typed backend list — a
      // sub-category and a category with the same name have different
      // UUIDs, and the rename autocomplete's picked "id" is just the
      // hardcoded suggestion string (id === name), which the backend
      // rejects as invalid UUID. Same shape as the tree's rename flow.
      const resolvedId = resolveSuggestionId(trimmedName, newSectionType);

      if (newSectionType === BundleTreeTypes.CATEGORY) {
        payload.newCategoryIndex = newSectionIndex;
        if (resolvedId) {
          payload.categoryId = resolvedId;
        } else {
          payload.categoryName = trimmedName;
        }
      } else if (newSectionType === BundleTreeTypes.SUB_CATEGORY) {
        if (!newSectionCategoryId) {
          toast.current?.show({
            severity: 'warn',
            detail: 'Unable to determine the parent category',
            life: 3000,
          });
          return;
        }

        payload.categoryId = newSectionCategoryId;
        payload.newSubCategoryIndex = newSectionIndex;
        if (resolvedId) {
          payload.subCategoryId = resolvedId;
        } else {
          payload.subCategoryName = trimmedName;
        }
      }
    } else if (selectedExistingSectionId && selectedExistingSectionType) {
      if (selectedExistingSectionType === BundleTreeTypes.CATEGORY) {
        payload.categoryId = selectedExistingSectionId;
      } else if (selectedExistingSectionType === BundleTreeTypes.SUB_CATEGORY) {
        const parentCategoryId = selectedExistingSectionCategoryId;
        if (!parentCategoryId) {
          toast.current?.show({
            severity: 'warn',
            detail: 'Unable to determine the parent category',
            life: 3000,
          });
          return;
        }

        payload.categoryId = parentCategoryId;
        payload.subCategoryId = selectedExistingSectionId;
      }
    } else {
      toast.current?.show({
        severity: 'warn',
        detail: 'Select where to move the pages',
        life: 3000,
      });
      return;
    }

    triggerAddBundlePageCategory(payload)
      .unwrap()
      .then(() => {
        handleHide();
        sendMessageWithoutResponse(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, {
          reason: 'Pages moved to section',
        });
        showPagesMovedToast({
          pageCount: pageIds.length,
          onJumpToPage: () => {
            // Resolved on click, not now: the refresh that follows the move
            // reindexes every page, and the toast outlives this dialog.
            const getPageIndexByPageId = makeSelectBundlePageIndexByPageId(store.getState());
            const visibleIndexes = pageIds.map(getPageIndexByPageId).filter((index) => index >= 0);

            if (!visibleIndexes.length) {
              globalToastService.show({
                severity: 'info',
                summary: 'Page hidden by filters',
                detail: 'This page is hidden by the filters applied to this bundle.',
              });
              return;
            }

            documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(Math.min(...visibleIndexes));
          },
        });
      })
      .catch((error) => {
        // The endpoint lock has already shown a toast for this
        if (isEndpointLockError(error)) return;
        toast.current?.show({
          severity: 'error',
          detail: error?.data?.message || 'Failed to move pages',
          life: 5000,
        });
      });
  }, [
    bundleId,
    documentReviewPageContext,
    handleHide,
    isNewSectionAdded,
    newSectionCategoryId,
    newSectionIndex,
    newSectionName,
    newSectionType,
    pageIds,
    providerId,
    resolveSuggestionId,
    selectedExistingSectionCategoryId,
    selectedExistingSectionId,
    selectedExistingSectionType,
    selectedSuggestionId,
    sendMessageWithoutResponse,
    store,
    triggerAddBundlePageCategory,
  ]);

  const isMoveDisabled = !isNewSectionAdded && !selectedExistingSectionId;

  useEffect(() => {
    if (isNewSectionNameAlreadyExistInCurrentType) {
      const notEmptyNewSectionName = newSectionName || DEFAULT_NEW_SECTION_NAME;

      toast.current?.show({
        severity: 'info',
        summary: 'Create new section',
        detail: `You cannot create a section with the name "${notEmptyNewSectionName}" because it is already in use.`,
        sticky: false,
        life: 5000,
      });

      setIsNewSectionNameAlreadyExistInCurrentType(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewSectionNameAlreadyExistInCurrentType]);

  return (
    <>
      <Toast ref={toast} />

      <Dialog
        visible={true}
        onHide={handleHide}
        style={{
          width: '90vw',
          maxWidth: '500px',
          borderRadius: '8px',
          border: '1px solid #e1e1e0',
          overflow: 'hidden',
        }}
        contentClassName="!p-0"
        headerClassName="!hidden"
      >
        <div className="border-fletchers-gray-110 flex items-center justify-between border-b px-[24px] py-[16px]">
          <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">Move to section</div>
          <button
            type="button"
            aria-label="Close"
            onClick={handleHide}
            className="cursor-pointer rounded p-1 focus:outline-none"
          >
            <CloseIcon width={12} height={12} />
          </button>
        </div>

        <div className="flex flex-col gap-[16px] px-[24px] py-[16px]">
          <div className="bg-fletchers-gray-50 rounded-md px-4 py-2">
            <span className="text-[14px]">
              <span className="font-semibold">
                {pageIds.length} {pageIds.length === 1 ? 'page' : 'pages'}
              </span>
              {' selected'}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold">Select or create a section</span>
            {isInsertMode && editingIndex === null && (
              <span className="text-fletchers-gray-175 text-[14px]">Hover between sections to choose a position</span>
            )}
            <div className="border-fletchers-gray-100 h-[320px] w-full overflow-y-auto rounded-md border bg-white">
              {/* Breadcrumb navigation */}
              {topLevelElement && (
                <div className="border-fletchers-gray-100 flex items-center gap-1 border-b px-3 py-2">
                  {showBackButton && (
                    <button
                      type="button"
                      onClick={() => {
                        handleInlineCancel();
                        clearExistingSelection();
                        handleBackToCategoriesClick();
                      }}
                      className="text-fletchers-green-500 hover:text-fletchers-green-800 mr-1 cursor-pointer"
                    >
                      <span className="pi pi-arrow-left text-[12px]"></span>
                    </button>
                  )}
                  <span className="text-fletchers-gray-175 text-[14px]">{topLevelElement.name}</span>
                </div>
              )}

              {/* Section list */}
              {currentListItems.map((item, index) => {
                const isNewlyInserted = item.id === newSectionId;
                const isSelectedExisting = selectedExistingSectionId === item.id;
                const isHighlighted = isNewlyInserted || isSelectedExisting;
                const isEditingHere = editingIndex === index;
                // Show the "drop indicator" line above this row when the
                // dragged new-section is hovering over it. Same visual
                // affordance as the sections tree cursor.
                const showDropLineBefore = isNewSectionAdded && dragOverIndex === index && !isNewlyInserted;

                return (
                  <div key={item.id}>
                    {/* Insert drop zone (thin line on hover, before this item) */}
                    {isInsertMode && (
                      <div
                        className="group relative flex h-[8px] cursor-pointer items-center px-3"
                        onClick={() => handleInsertAtPosition(index)}
                      >
                        <div className="bg-fletchers-green-500 h-[2px] w-full rounded opacity-0 transition-opacity group-hover:opacity-100" />
                        <div className="bg-fletchers-green-500 absolute top-1/2 left-1/2 flex h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-[12px] leading-none text-white opacity-0 transition-opacity group-hover:opacity-100">
                          +
                        </div>
                      </div>
                    )}

                    {/* Drop indicator (during drag reorder). Matches the
                        1.5px line the sections tree draws between rows. */}
                    {showDropLineBefore && <div className="bg-fletchers-green-500 mx-3 h-[1.5px] rounded" />}

                    {/* Inline editor (appears at this position when editing) */}
                    {isEditingHere && (
                      <div className="border-fletchers-green-500 border-l-2 px-3 py-2">
                        <SectionRenameInput
                          placeholder="Enter section name"
                          suggestions={renameSuggestions}
                          onCommit={handleInlineSubmit}
                          onCancel={handleInlineCancel}
                        />
                      </div>
                    )}

                    {/* Section item row */}
                    <div
                      className={classNames('flex items-center justify-between px-3 py-2 transition-colors', {
                        'bg-fletchers-green-50 border-fletchers-green-300 border-l-2': isHighlighted,
                        'hover:bg-fletchers-gray-50 cursor-pointer':
                          !isHighlighted && !isNewlyInserted && !isInsertMode,
                        'border-l-2 border-l-transparent': !isHighlighted,
                        // Grab cursor only on the confirmed new-section
                        // row — the only draggable item in this dialog.
                        '!cursor-grab active:!cursor-grabbing': isNewlyInserted && !isEditingHere,
                      })}
                      draggable={isNewlyInserted && !isEditingHere}
                      onDragStart={(e) => {
                        if (!isNewlyInserted) return;
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', item.id);
                      }}
                      onDragEnd={() => setDragOverIndex(null)}
                      onDragOver={(e) => {
                        if (!isNewSectionAdded || isNewlyInserted) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOverIndex !== index) setDragOverIndex(index);
                      }}
                      onDrop={(e) => {
                        if (!isNewSectionAdded || isNewlyInserted) return;
                        e.preventDefault();
                        setDragOverIndex(null);
                        moveNewSectionToIndex(index);
                      }}
                      onClick={() => {
                        if (!isInsertMode && !isNewlyInserted) {
                          handleExistingSectionClick(item);
                        }
                      }}
                    >
                      <span
                        className={classNames('flex items-center gap-2 text-[14px] font-semibold', {
                          'text-fletchers-green-500': isHighlighted,
                          'text-fletchers-black': !isHighlighted,
                        })}
                      >
                        {isNewlyInserted && !isEditingHere && (
                          // Three-line "drag handle" glyph, purely visual —
                          // the whole row is the drag source (draggable=true
                          // on the parent), so the icon just signals intent.
                          <span
                            className="pi pi-bars text-fletchers-green-500 text-[12px] leading-none"
                            aria-hidden="true"
                          />
                        )}
                        {item.name}
                      </span>
                      <div className="flex items-center gap-1">
                        {isNewlyInserted && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveInsertedSection(item);
                            }}
                            className="text-fletchers-gray-200 hover:text-fletchers-black cursor-pointer p-0.5"
                          >
                            <span className="pi pi-times text-[10px]"></span>
                          </button>
                        )}
                        {!isInsertMode &&
                          item.children &&
                          item.children.length > 0 &&
                          item.type !== BundleTreeTypes.SUB_CATEGORY && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInlineCancel();
                                clearExistingSelection();
                                handleOpenSubcategoriesClick(item);
                              }}
                              className="text-fletchers-gray-200 hover:text-fletchers-green-500 cursor-pointer p-0.5"
                            >
                              <ChevronIcon className="-rotate-90 !stroke-current" width={10} height={10} />
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Insert drop zone after last item */}
              {isInsertMode && (
                <div
                  className="group relative flex h-[8px] cursor-pointer items-center px-3"
                  onClick={() => handleInsertAtPosition(currentListItems.length)}
                >
                  <div className="bg-fletchers-green-500 h-[2px] w-full rounded opacity-0 transition-opacity group-hover:opacity-100" />
                  <div className="bg-fletchers-green-500 absolute top-1/2 left-1/2 flex h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-[12px] leading-none text-white opacity-0 transition-opacity group-hover:opacity-100">
                    +
                  </div>
                </div>
              )}

              {/* Inline editor at end (when inserting after last item) */}
              {editingIndex === currentListItems.length && (
                <div className="border-fletchers-green-500 border-l-2 px-3 py-2">
                  <SectionRenameInput
                    placeholder="Enter section name"
                    suggestions={renameSuggestions}
                    onCommit={handleInlineSubmit}
                    onCancel={handleInlineCancel}
                  />
                </div>
              )}

              {/* Drop target after the last row — without this the user
                  can only reorder above/onto existing rows, never to the
                  very bottom. Height picks up the remaining list space so
                  the drop area is easy to hit. */}
              {isNewSectionAdded && (
                <div
                  className="min-h-[24px] flex-1"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverIndex !== currentListItems.length) setDragOverIndex(currentListItems.length);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverIndex(null);
                    moveNewSectionToIndex(currentListItems.length - 1);
                  }}
                >
                  {dragOverIndex === currentListItems.length && (
                    <div className="bg-fletchers-green-500 mx-3 h-[1.5px] rounded" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 px-[24px] pb-[24px]">
          {!isInsertMode && !isNewSectionAdded && editingIndex === null && (
            <OutlineButton
              className="!h-[32px]"
              onClick={() => {
                clearExistingSelection();
                // Drop straight into the inline editor at the bottom of
                // the list — the reorder-by-drag step below lets the user
                // move it wherever they want after naming it, so there's
                // no need for the between-sections hover-line picker.
                handleInsertAtPosition(currentListItems.length);
              }}
            >
              + New section
            </OutlineButton>
          )}
          {(isInsertMode || editingIndex !== null) && (
            <OutlineButton
              className="!h-[32px]"
              onClick={() => {
                if (isNewSectionAdded) {
                  removeNewInlineSection();
                }
                handleInlineCancel();
                setIsInsertMode(false);
              }}
            >
              Back
            </OutlineButton>
          )}
          <div className="ml-auto flex gap-2">
            <OutlineButton className="!h-[32px]" onClick={handleHide}>
              Cancel
            </OutlineButton>
            <GreenButton className="!h-[32px]" onClick={handleMove} disabled={isMoveDisabled}>
              Confirm
            </GreenButton>
          </div>
        </div>
      </Dialog>
    </>
  );
};
